import { AUTH_DENIAL_MESSAGES, getAuthDenialCode } from './authDenial'

export type LoginProvider = 'password' | 'microsoft.com'

const ADMIN = 'Fale com um administrador.'

const AAD_CODE = /AADSTS\d+/

/**
 * Microsoft (Entra ID) errors that reach the app through Firebase when it
 * exchanges the sign-in for a token. All of them are setup problems the user
 * cannot fix, so the message says who can.
 */
const AAD_MESSAGES: Record<string, string> = {
    AADSTS7000222: `O segredo do aplicativo Microsoft venceu. ${ADMIN}`,
    AADSTS7000215: `O segredo do aplicativo Microsoft está incorreto. ${ADMIN}`,
    AADSTS700016: `O aplicativo Microsoft não foi encontrado no diretório da empresa. ${ADMIN}`,
    AADSTS50011: `O endereço de retorno do aplicativo Microsoft não está configurado. ${ADMIN}`,
    AADSTS65001: `O aplicativo Microsoft precisa da aprovação de um administrador. ${ADMIN}`,
    AADSTS50020: 'Esta conta Microsoft não pertence à organização. Use sua conta corporativa.',
    AADSTS50105: `Sua conta Microsoft não tem acesso a este aplicativo. ${ADMIN}`,
}

/**
 * Firebase sign-in errors mapped to what the user can act on
 * (contracts/ui-auth.md §2). `null` means the user cancelled: no message.
 */
const FIREBASE_MESSAGES: Record<string, string | null> = {
    'auth/popup-blocked':
        'Seu navegador bloqueou a janela de login. Permita pop-ups para este site e tente de novo.',
    'auth/popup-closed-by-user': null,
    'auth/cancelled-popup-request': null,
    'auth/user-cancelled': null,
    'auth/account-exists-with-different-credential': `Sua conta Microsoft ainda não foi vinculada ao seu cadastro. ${ADMIN}`,
    'auth/user-disabled': AUTH_DENIAL_MESSAGES.USER_DISABLED,
    'auth/too-many-requests':
        'Muitas tentativas de login. Aguarde alguns minutos e tente de novo.',
    'auth/network-request-failed':
        'Sem conexão com a internet. Verifique sua rede e tente de novo.',
    'auth/operation-not-allowed': `Este método de login não está habilitado. ${ADMIN}`,
    'auth/unauthorized-domain': `Este endereço não está autorizado a fazer login. ${ADMIN}`,
}

const WRONG_CREDENTIALS = 'E-mail ou senha incorretos.'
const PASSWORD_CODES = [
    'auth/invalid-credential',
    'auth/wrong-password',
    'auth/user-not-found',
    'auth/invalid-email',
    'auth/missing-password',
]

/** What the API says when it answers but not with one of the session codes. */
const API_MESSAGES: Record<string, string> = {
    INVALID_TOKEN: 'Não foi possível validar sua sessão. Tente entrar de novo.',
    UNSUPPORTED_PROVIDER: `Este método de login não é aceito. ${ADMIN}`,
    IDENTITY_LOOKUP_FAILED:
        'Não foi possível verificar seu acesso agora. Tente de novo em instantes.',
}

interface ErrorShape {
    code?: string
    message?: string
    response?: { status?: number; data?: { code?: unknown } }
}

const withCode = (message: string, code?: string) =>
    code ? `${message} (código: ${code})` : message

const apiMessage = (error: ErrorShape): string | null => {
    const response = error.response
    if (!response) return null

    const apiCode = response.data?.code
    if (typeof apiCode === 'string' && apiCode in API_MESSAGES) {
        return API_MESSAGES[apiCode]
    }

    const status = response.status ?? 0
    if (status === 403) return `Você não tem permissão para entrar. ${ADMIN}`
    if (status >= 500) {
        return 'O servidor está indisponível no momento. Tente de novo em instantes.'
    }
    return null
}

/**
 * The message for a failed sign-in, or null when nothing should be shown
 * (the user closed the window).
 */
export const loginErrorMessage = (
    error: unknown,
    provider: LoginProvider = 'password'
): string | null => {
    const denial = getAuthDenialCode(error)
    if (denial) return AUTH_DENIAL_MESSAGES[denial]

    const shape = (error ?? {}) as ErrorShape

    const fromApi = apiMessage(shape)
    if (fromApi) return fromApi

    // The request never got an answer: axios reports it as a network error.
    if (shape.code === 'ERR_NETWORK' || (shape.message === 'Network Error' && !shape.response)) {
        return 'Não foi possível conectar ao servidor. Verifique sua internet e tente de novo.'
    }

    const code = shape.code
    if (code && code in FIREBASE_MESSAGES) return FIREBASE_MESSAGES[code]

    if (code && PASSWORD_CODES.includes(code)) {
        if (provider === 'password') return WRONG_CREDENTIALS
        // A rejected Microsoft token exchange: the reason is in the AADSTS code.
        const aad = shape.message?.match(AAD_CODE)?.[0]
        if (aad && aad in AAD_MESSAGES) return AAD_MESSAGES[aad]
        return withCode(
            `Não foi possível entrar com a Microsoft. ${ADMIN}`,
            aad ?? code
        )
    }

    return withCode(
        'Não foi possível entrar. Tente de novo; se continuar, fale com um administrador.',
        code
    )
}
